import { useApolloClient } from '@apollo/client';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { min, max } from 'lodash';
import { formattedBillingAddress } from '~/modules/billing-invoicing/bill/formattedBillingAddress';
import { useMeContext } from '~/modules/me';
import {
  toRepliconDate,
  mapRepliconDateToMidnightUTCString,
  mapRepliconDateToUtcObject,
  dateToMidnightUTCObject
} from '~/modules/common/dates/convert';
import { useBulkCreateBillFromBillItemsBatch } from './useBulkCreateBillFromBillItemsBatch';
import { useFetchClientsDefaultData } from './useFetchClientsDefaultData';
import { useFetchProjectsBillingSettings } from './useFetchProjectsBillingSettings';
import { useFetchInvoiceDefaultTemplate } from './useFetchInvoiceDefaultTemplate';
import { useBulkGetProjectOrClientBillingDefaultsForAvailableToBill } from './useBulkProjectOrClientBillingDefaultsForAvailableToBill';

export const getUniqueClientIds = selectedAvailableToBillItems => [
  ...new Set(selectedAvailableToBillItems.map(({ client }) => client.id))
];
export const getUniqueProjectIds = selectedAvailableToBillItems => [
  ...new Set(selectedAvailableToBillItems.map(({ project }) => project.id))
];

const getValueOrDefault = value => (value ? [value] : null);

const mapToBulkCreateBillInput = ({
  billingItem: {
    startDate: itemStartDate,
    endDate: itemEndDate,
    client: { id: clientId },
    project,
    projectLeader
  },
  availableToBillFilter,
  defaultInvoiceTemplate,
  projectOrClientBillingSetting,
  projectUris,
  me: { baseCurrency, featureFlags }
}) => {
  const { isPsaPrpAddProjectColumnOnAvailableToBillEnabled } = featureFlags;
  const { projects, projectManagers, dateRange } = availableToBillFilter || {};
  const {
    poNumber,
    defaultInvoicePaymentTerm = 30,
    invoiceCurrency,
    taxProfile,
    invoiceTemplate,
    billLineItemsBy,
    description,
    internalNotes,
    showComments,
    billingContactInfo
  } = projectOrClientBillingSetting || {};

  const issueDate = DateTime.local();
  const columns = billLineItemsBy || ['PROJECT'];

  const startDate = isPsaPrpAddProjectColumnOnAvailableToBillEnabled
    ? max(
        [
          typeof dateRange?.startDate === 'string'
            ? dateToMidnightUTCObject(dateRange?.startDate)
            : dateRange?.startDate,
          itemStartDate && mapRepliconDateToUtcObject(itemStartDate)
        ].filter(Boolean)
      )
    : itemStartDate;

  const endDate = isPsaPrpAddProjectColumnOnAvailableToBillEnabled
    ? min(
        [
          typeof dateRange?.endDate === 'string'
            ? dateToMidnightUTCObject(dateRange?.endDate)
            : dateRange?.endDate,
          itemEndDate && mapRepliconDateToUtcObject(itemEndDate)
        ].filter(Boolean)
      )
    : itemEndDate;

  return {
    billInput: {
      billingAddress:
        typeof billingContactInfo === 'object'
          ? formattedBillingAddress(billingContactInfo)
          : '',
      description,
      clientUri: clientId,
      currencyUri: invoiceCurrency?.id || baseCurrency.id,
      poNumber,
      issueDate: toRepliconDate(issueDate),
      paymentTerms: defaultInvoicePaymentTerm,
      dueDate: toRepliconDate(
        issueDate.plus({ days: defaultInvoicePaymentTerm })
      ),
      invoiceTemplateUri: invoiceTemplate?.id || defaultInvoiceTemplate?.id,
      taxProfileUri: taxProfile?.id,
      period: {
        startDate: itemStartDate,
        endDate: itemEndDate
      },
      summarizeColumn: columns,
      internalNotes,
      showComments
    },
    billFromBillingItemsBatchParameter: {
      billingItemColumnOption: columns,
      billingItemSearch: {
        projectUris: isPsaPrpAddProjectColumnOnAvailableToBillEnabled
          ? getValueOrDefault(project?.id) || projectUris || projects
          : projectUris,
        projectLeaderUris: isPsaPrpAddProjectColumnOnAvailableToBillEnabled
          ? getValueOrDefault(projectLeader?.id) || projectManagers
          : null,
        dateRange: {
          startDate: mapRepliconDateToMidnightUTCString(startDate),
          endDate: mapRepliconDateToMidnightUTCString(endDate)
        },
        excludeZeroAmounts: true
      }
    },
    allowEmptyInvoice: false
  };
};

export const useBulkCreateBill = ({
  availableToBillFilter,
  selectedAvailableToBillItems,
  setBillingBatchState,
  projectUri
}) => {
  const apolloClient = useApolloClient();
  const me = useMeContext();
  const { isPsaPrpAddProjectColumnOnAvailableToBillEnabled } = me.featureFlags;

  const [bulkBillCreationInitiated, setBulkBillCreationInitiated] = useState(
    false
  );
  const {
    bulkCreateBillFromBillingItemsBatch,
    loading
  } = useBulkCreateBillFromBillItemsBatch({
    setBillingBatchState
  });

  const { fetchClientsDefaultData } = useFetchClientsDefaultData({
    apolloClient
  });

  const { fetchProjectsBillingSettings } = useFetchProjectsBillingSettings({
    apolloClient
  });

  const {
    fetchBulkGetProjectBillingDefaultsForAvailableToBill
  } = useBulkGetProjectOrClientBillingDefaultsForAvailableToBill(apolloClient);

  const { fetchInvoiceDefaultTemplate } = useFetchInvoiceDefaultTemplate({
    apolloClient
  });

  return {
    bulkCreateBill: async () => {
      const clientIds = getUniqueClientIds(selectedAvailableToBillItems);

      if (!clientIds?.length) return;

      setBulkBillCreationInitiated(true);
      const [
        projectOrClientBillingSettings,
        defaultInvoiceTemplate
      ] = await Promise.all([
        fetchBulkGetProjectBillingDefaultsForAvailableToBill(
          selectedAvailableToBillItems.map(item => ({
            clientUris: [item.client.id],
            projectUris: isPsaPrpAddProjectColumnOnAvailableToBillEnabled
              ? projectUri
                ? [projectUri]
                : item.project?.id
                ? [item.project?.id]
                : undefined
              : projectUri
              ? [projectUri]
              : undefined,
            billingPeriod: {
              startDate: mapRepliconDateToMidnightUTCString(item.startDate),
              endDate: mapRepliconDateToMidnightUTCString(item.endDate)
            }
          }))
        ),
        fetchInvoiceDefaultTemplate()
      ]);

      bulkCreateBillFromBillingItemsBatch(
        selectedAvailableToBillItems
          .filter(item => item.startDate && item.endDate)
          .map((item, index) =>
            mapToBulkCreateBillInput({
              billingItem: item,
              defaultInvoiceTemplate,
              projectOrClientBillingSetting:
                projectOrClientBillingSettings[index],
              projectUris: isPsaPrpAddProjectColumnOnAvailableToBillEnabled
                ? getValueOrDefault(projectUri || item.project?.id)
                : getValueOrDefault(projectUri),
              me
            })
          )
      );
      setBulkBillCreationInitiated(false);
    },
    bulkCreateBill2: async () => {
      const clientIds = getUniqueClientIds(selectedAvailableToBillItems);

      const projectIds = getUniqueProjectIds(
        selectedAvailableToBillItems.filter(({ project }) => project)
      );

      if (clientIds.length === 0 && projectIds.length === 0) return;

      setBulkBillCreationInitiated(true);

      const [
        clientDetailsById,
        projectsBillingSettingsById,
        defaultInvoiceTemplate
      ] = await Promise.all([
        fetchClientsDefaultData(clientIds),
        fetchProjectsBillingSettings(projectIds),
        fetchInvoiceDefaultTemplate()
      ]);

      bulkCreateBillFromBillingItemsBatch(
        selectedAvailableToBillItems
          .filter(item => item.startDate && item.endDate)
          .map(item =>
            mapToBulkCreateBillInput({
              billingItem: item,
              defaultInvoiceTemplate,
              projectOrClientBillingSetting: {
                ...(clientDetailsById[item.client.id] || {}),
                ...(clientDetailsById[item.client.id]?.billingSettings || {}),
                ...(projectsBillingSettingsById[item.project?.id] || {})
              },
              projectUris: getValueOrDefault(projectUri || item.project?.id),
              availableToBillFilter,
              me
            })
          )
      );
      setBulkBillCreationInitiated(false);
    },
    loading: bulkBillCreationInitiated || loading
  };
};
